import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  line, Color, fill, LIST_PAGES, RAISE_PURCHASE_ORDER_ACT, RAISE_WORK_ORDER_ACT, Size, t,
  type SalesOrderLineRow, type SalesOrderRaiseView, type SalesOrderRaised, type SalesOrderShortLine, type SuggestionRow,
} from '@bms2/shared';
import { AlertBanner, Button, Checkbox, Dropdown, Select, StatusPill } from '../../components/controls';
import { useStatusResolver } from '../../shell/StatusResolver';
import { Modal } from '../../components/modals/Modal';
import { PURCHASE_ORDERS_PATH, SALES_ORDERS_PATH, apiFetch, post, useRows } from './api';
import styles from './SalesOrderRaise.module.css';
import { useAddNew } from './AddNewHost';

/** 🔒 The order's raise routes, relative to the API root (decision 0863); registered with the path guard. */
export const salesOrderShortfallPath = (orderId: string, siteId: string) => `${SALES_ORDERS_PATH}/${orderId}/shortfall?siteId=${encodeURIComponent(siteId)}`;
export const raisePurchaseOrderPath = (orderId: string) => `${SALES_ORDERS_PATH}/${orderId}/raise-purchase-order`;
export const raiseWorkOrderPath = (orderId: string) => `${SALES_ORDERS_PATH}/${orderId}/raise-work-order`;

type RaiseWindow = 'purchaseOrder' | 'workOrder' | null;

/** The window the URL asks for: the order's Work Order to-do opens the same raise the footer bar offers (decisions 0863, 1440). */
export function raiseWindowOf(act: string | null): RaiseWindow {
  if (act === RAISE_WORK_ORDER_ACT) return 'workOrder';
  if (act === RAISE_PURCHASE_ORDER_ACT) return 'purchaseOrder';
  return null;
}

/**
 * 🔒 The Sales Order's two raises in its footer bar, outlined (decision 1440,
 * moving them out of the title bar; decision 0863, the owner's T282): *"sales orders should allow creation of purchase order and or drop ship
 * and work orders"*. Each is a button until it has raised its document, and then
 * reads *Opened* with a link to it. 🔨 A status that raises nothing leaves both
 * buttons standing, disabled, with the server's own reason on them.
 */
export function SalesOrderRaiseControls({ orderId, view, lines, askedFor = null, apiBaseUrl = '' }: {
  orderId: string;
  view: SalesOrderRaiseView;
  lines: readonly SalesOrderLineRow[];
  /** The window the URL asked for, opened once. */
  askedFor?: RaiseWindow;
  apiBaseUrl?: string;
}) {
  const [chosen, setChosen] = useState<RaiseWindow | 'none'>('none');
  // 🔨 The URL's window opens once, and only where that raise still stands open to the order.
  const asked = askedFor === 'workOrder' && (view.workOrder || view.refusal || view.serviceLineCount === 0) ? null : askedFor === 'purchaseOrder' && (view.purchaseOrder || view.refusal) ? null : askedFor;
  const open: RaiseWindow = chosen === 'none' ? asked : chosen;
  const close = () => setChosen(null);
  const resolveStatus = useStatusResolver();
  // 🔒 The link carries the raised document's own status pill, so a Draft job never reads Opened (decision 10745; T982); a caller that names no status keeps the word.
  const opened = (label: string, raised: SalesOrderRaised, route: string, kind: string, entityType: string) => (
    <span className={styles.opened} data-sales-order-raised={kind}>
      {label}
      <Link to={`${route}/${raised.id}`} className={styles.recordLink}>{raised.status ? raised.number : line(raised.number, '·', t('salesOrder.raise.opened'))}</Link>
      {raised.status ? <StatusPill entityType={entityType} statusValue={raised.status} resolve={resolveStatus} size={Size.Small} /> : null}
    </span>
  );
  const refused = view.refusal ?? undefined;
  const carriesService = view.serviceLineCount > 0;
  return (
    <span className={styles.bar} data-sales-order-raise="true">
      {view.purchaseOrder ? opened(t('salesOrder.raise.purchaseOrderOpened'), view.purchaseOrder, LIST_PAGES.PurchaseOrder.route, 'purchaseOrder', 'PurchaseOrderStatus') : (
        <span data-sales-order-raise-action="purchaseOrder">
          <Button size={Size.Medium} color={Color.Inactive} text={[t('salesOrder.raise.purchaseOrder')]} actions={[() => setChosen('purchaseOrder')]} disabled={refused !== undefined} {...(refused ? { disabledReason: refused } : {})} />
        </span>
      )}
      {/* 🔨 An order with no service line offers no Raise a work order, which could only be refused (T926, the data-entry snags; decision 1713). */}
      {view.workOrder ? opened(t('salesOrder.raise.workOrderOpened'), view.workOrder, LIST_PAGES.WorkOrder.route, 'workOrder', 'WorkOrderStatus') : !carriesService ? null : (
        <span data-sales-order-raise-action="workOrder">
          <Button size={Size.Medium} color={Color.Inactive} text={[t('salesOrder.raise.workOrder')]} actions={[() => setChosen('workOrder')]} disabled={refused !== undefined} {...(refused ? { disabledReason: refused } : {})} />
        </span>
      )}
      {/* 🔨 Mounted only while open, so each opening reads the order afresh (the modal-initial trap). */}
      {open === 'purchaseOrder' ? <RaisePurchaseOrderWindow orderId={orderId} canDropShip={view.canDropShip} apiBaseUrl={apiBaseUrl} onCancel={close} /> : null}
      {open === 'workOrder' ? <RaiseWorkOrderWindow orderId={orderId} lines={lines} apiBaseUrl={apiBaseUrl} onCancel={close} /> : null}
    </span>
  );
}

/**
 * 🔒 Raise a purchase order (decision 0863): the vendor, our site — whose stock is
 * used first and where the order is received — and the drop-ship choice, off by
 * default; the lines short of stock at that site listed as it is chosen.
 */
function RaisePurchaseOrderWindow({ orderId, canDropShip, apiBaseUrl, onCancel }: { orderId: string; canDropShip: boolean; apiBaseUrl: string; onCancel: () => void }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const sites = useRows<{ id: string; label: string }>(`${PURCHASE_ORDERS_PATH}/receiving-sites`, apiBaseUrl);
  const siteRows = sites.state.kind === 'ready' ? sites.state.data : [];
  const [site, setSite] = useState('');
  // One site is the only choice, so it is taken, as the purchase order's own form takes it.
  const chosenSite = site || (siteRows.length === 1 ? siteRows[0]!.id : '');
  const [vendor, setVendor] = useState<{ id: string; label: string } | null>(null);
  const [dropShip, setDropShip] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const short = useRows<SalesOrderShortLine>(chosenSite ? salesOrderShortfallPath(orderId, chosenSite) : null, apiBaseUrl);
  const shortRows = short.state.kind === 'ready' ? short.state.data : [];

  const vendorSearch = useCallback(async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PURCHASE_ORDERS_PATH}/vendors?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; displayName: string }[] };
    return body.rows.map((row) => ({ id: row.id, line1: row.displayName }));
  }, [apiBaseUrl]);

  const raise = async () => {
    if (!vendor) return setError(t('salesOrder.raise.needsVendor'));
    if (!chosenSite) return setError(t('salesOrder.raise.chooseSite'));
    setBusy(true);
    const answer = await post<SalesOrderRaised>(raisePurchaseOrderPath(orderId), { vendorCompanyId: vendor.id, siteId: chosenSite, dropShip }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    // 🔒 The Draft is raised and the person lands on it, the way a create always does.
    navigate(`${LIST_PAGES.PurchaseOrder.route}/${answer.data.id}`);
  };

  return (
    <Modal
      open
      title={t('salesOrder.raise.purchaseOrder')}
      onCancel={onCancel}
      dataModal="sales-order-raise-purchase-order"
      actions={[
        { kind: 'primary', label: t('salesOrder.raise.raise'), onAct: () => void raise(), disabled: busy },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <Dropdown size={Size.Medium} label={t('salesOrder.raise.vendor')} entityLabel={t('salesOrder.raise.vendor')} search={vendorSearch} value={vendor?.label ?? null} onSelect={(row) => setVendor({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'vendor' }, (row) => setVendor({ id: row.id, label: row.line1 }))} required />
      <Select size={Size.Medium} label={t('salesOrder.raise.site')} value={chosenSite} onChange={setSite} options={siteRows.map((row) => ({ value: row.id, label: row.label }))} hint={t('salesOrder.raise.siteHint')} dataAttribute={{ 'data-sales-order-raise-site': 'true' }} required />
      {canDropShip ? (
        <span data-sales-order-raise-drop-ship={dropShip ? 'on' : 'off'}>
          <Checkbox label={t('salesOrder.raise.dropShip')} checked={dropShip} onChange={setDropShip} />
        </span>
      ) : (
        <p className={styles.muted} data-sales-order-raise-drop-ship="unavailable">{t('salesOrder.raise.noDropShip')}</p>
      )}
      <section data-sales-order-raise-short={shortRows.length}>
        <h3>{t('salesOrder.raise.shortTitle')}</h3>
        {!chosenSite ? <p className={styles.muted}>{t('salesOrder.raise.chooseSite')}</p> : short.state.kind === 'ready' && shortRows.length === 0 ? <p className={styles.muted}>{t('salesOrder.raise.noneShort')}</p> : short.state.kind === 'empty' ? <p className={styles.muted}>{t('salesOrder.raise.noneShort')}</p> : null}
        <ul className={styles.list}>
          {shortRows.map((row) => (
            <li key={row.lineId} className={styles.row} data-sales-order-short-line={row.lineId}>
              <span>{row.sku ? line(row.sku, '·', row.description) : row.description}</span>
              <span>
                {line(String(row.shortQuantity), row.unitName)}
                {row.fromStock > 0 ? <span className={styles.muted} data-sales-order-from-stock={row.fromStock}>{fill(t('salesOrder.raise.fromStock'), { count: String(row.fromStock) })}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      </section>
      {addNew.host}
    </Modal>
  );
}

/**
 * 🔒 Raise a work order (decision 0863): the order's service lines, each with the
 * budget the sale carried (decisions 0730, 0860), raised as a Draft at the order's
 * ship-to, or its bill-to where it ships nowhere.
 */
function RaiseWorkOrderWindow({ orderId, lines, apiBaseUrl, onCancel }: { orderId: string; lines: readonly SalesOrderLineRow[]; apiBaseUrl: string; onCancel: () => void }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const services = lines.filter((row) => row.isService === true);
  const raise = async () => {
    setBusy(true);
    const answer = await post<SalesOrderRaised>(raiseWorkOrderPath(orderId), {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    navigate(`${LIST_PAGES.WorkOrder.route}/${answer.data.id}`);
  };
  return (
    <Modal
      open
      title={t('salesOrder.raise.workOrder')}
      onCancel={onCancel}
      dataModal="sales-order-raise-work-order"
      actions={[
        { kind: 'primary', label: t('salesOrder.raise.raise'), onAct: () => void raise(), disabled: busy || services.length === 0 },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <h3>{t('salesOrder.raise.serviceTitle')}</h3>
      {services.length === 0 ? <p className={styles.muted}>{t('salesOrder.raise.noService')}</p> : null}
      <ul className={styles.list} data-sales-order-raise-services={services.length}>
        {services.map((row) => (
          <li key={row.id} className={styles.row} data-sales-order-service-line={row.id}>
            <span>{row.sku ? line(row.sku, '·', row.description) : row.description}</span>
            <span data-sales-order-service-budget={row.budgetedHours ?? ''}>
              {/* 🔒 A typed budget is what the job carries; with none, a line sold in hours budgets its quantity, so the quantity is drawn (decision 0860). */}
              {row.budgetedHours === null || row.budgetedHours === undefined ? line(String(row.quantity), row.unitName) : line(t('salesOrder.raise.budget'), `${row.budgetedHours}${t('salesOrder.raise.hours')}`)}
            </span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
